import {
  Plus, ScrollText, Store, UserX, ClipboardPlus, Clock, ShoppingCart, BarChart3, AlertTriangle, ChevronRight
} from 'lucide-react';
import './PersonnelMobileHome.css';

// iOS WebView Personel ana görünümü (/mobile/personel → PersonnelManagement mobileApp modu).
// Satır hesapları PersonnelManagement'in getEmployeeRow'undan gelir (web tablosuyla aynı fonksiyon);
// burada yalnızca telefon yerleşimi var. Karta dokununca web'deki personel düzenleme modalı açılır,
// alttaki düğmeler web'deki satır işlemleriyle (yoklama, mesai, satış geçmişi, maaş detay) aynıdır.

const TABS = [
  { key: 'employees', label: 'Personel' },
  { key: 'attendance', label: 'Devam' },
  { key: 'salary', label: 'Maaş' },
  { key: 'bonus', label: 'Prim' },
  { key: 'formulas', label: 'Formüller' },
];

const tl = (n) => `₺${Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}`;

// Sekmeye göre kartta gösterilecek metrikler (web'deki sekme kolon setleriyle aynı bilgiler)
const metricsFor = (tab, row) => {
  const { salary, monthlyAbsent, totalAbsent, remainingLeave, weeklyHrs, monthlyHrs, formulaName } = row;
  const deductions = Number(salary.deductions || 0);
  const overtime = Number(salary.overtime_amount || 0);
  const bonus = Number(salary.formula_bonus_amount || 0);
  const refunds = Number(salary.refunds_total || 0);
  if (tab === 'attendance') {
    return [
      { label: 'Devamsızlık (Ay)', value: `${monthlyAbsent} gün`, tone: monthlyAbsent > 0 ? 'red' : '' },
      { label: 'Devamsızlık (Top.)', value: `${totalAbsent} gün`, tone: totalAbsent > 5 ? 'amber' : '' },
      { label: 'Kalan İzin', value: `${remainingLeave} gün`, tone: remainingLeave <= 3 ? 'red' : remainingLeave <= 7 ? 'amber' : 'green' },
      { label: 'Haftalık', value: weeklyHrs > 0 ? `${weeklyHrs.toFixed(1)} sa` : '—' },
      { label: 'Aylık', value: monthlyHrs > 0 ? `${Math.round(monthlyHrs)} sa` : '—' },
      { label: 'Ek Mesai', value: overtime > 0 ? `${salary.overtime_hours} sa` : '—', tone: overtime > 0 ? 'blue' : '' },
    ];
  }
  if (tab === 'salary') {
    return [
      { label: 'Maaş', value: tl(salary.base_salary || row.employee.salary) },
      { label: 'Kesinti', value: deductions > 0 ? `−${tl(deductions)}` : '—', tone: deductions > 0 ? 'red' : '' },
      { label: 'Ek Mesai', value: overtime > 0 ? `+${tl(overtime)}` : '—', tone: overtime > 0 ? 'blue' : '' },
      { label: 'Prim', value: bonus > 0 ? `+${tl(bonus)}` : '—', tone: bonus > 0 ? 'green' : '' },
    ];
  }
  if (tab === 'bonus') {
    return [
      { label: 'Toplam Satış', value: tl(salary.gross_sales_total) },
      { label: 'İade', value: refunds > 0 ? `−${tl(refunds)}` : '—', tone: refunds > 0 ? 'red' : '' },
      { label: 'Net Satış', value: tl(salary.bonus_eligible_sales_total ?? salary.sales_total), tone: 'purple' },
      { label: 'Formül', value: formulaName || '—', tone: formulaName ? 'purple' : '' },
    ];
  }
  return [
    { label: 'Maaş', value: tl(salary.base_salary || row.employee.salary) },
    { label: 'Net Satış', value: tl(salary.bonus_eligible_sales_total ?? salary.sales_total), tone: 'purple' },
    { label: 'Prim', value: bonus > 0 ? `+${tl(bonus)}` : '—', tone: bonus > 0 ? 'green' : '' },
    { label: 'Devamsızlık', value: `${monthlyAbsent} gün`, tone: monthlyAbsent > 0 ? 'red' : '' },
  ];
};

const PersonnelMobileHome = ({
  tab, setTab,
  month, setMonth, year, setYear, monthNames, years,
  loading, groups, imageHost,
  onOpen, onNew, onOpenLog,
  onAbsentToday, onAttendance, onOvertime, onSalesHistory, onSalaryDetail,
}) => {
  const employeeCount = groups.reduce((n, g) => n + g.employees.length, 0);
  const isTableTab = tab !== 'formulas';

  return (
    <div className="pmh">
      {/* Sekmeler */}
      <div className="pmh-tabs" role="tablist">
        {TABS.map(t => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={tab === t.key ? 'is-active' : ''}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isTableTab && (
        <>
          {/* Dönem + işlemler */}
          <div className="pmh-toolbar">
            <div className="pmh-period">
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                {monthNames.map((name, i) => <option key={name} value={i + 1}>{name}</option>)}
              </select>
              <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <button type="button" className="pmh-icon-btn" onClick={onOpenLog} aria-label="İşlem geçmişi">
              <ScrollText size={18} />
            </button>
            <button type="button" className="pmh-new" onClick={onNew}>
              <Plus size={17} /> Yeni
            </button>
          </div>

          {loading && employeeCount === 0 ? (
            <div className="pmh-group">
              {[0, 1, 2].map(i => <div key={i} className="pmh-card pmh-skeleton" />)}
            </div>
          ) : employeeCount === 0 ? (
            <div className="pmh-empty">
              <div className="pmh-empty-title">Henüz personel kaydı yok</div>
              <button type="button" className="pmh-new" onClick={onNew}><Plus size={17} /> İlk Personeli Ekle</button>
            </div>
          ) : (
            groups.map(group => (
              <div key={group.storeId} className="pmh-group">
                <div className="pmh-store">
                  <span><Store size={14} /> {group.name}</span>
                  <span className="pmh-store-count">{group.employees.length} kişi</span>
                </div>
                {group.employees.map(({ employee, row }) => {
                  const total = Number(row.salary.total_amount || 0);
                  const showAttendance = tab === 'employees' || tab === 'attendance';
                  const showSales = tab === 'employees' || tab === 'bonus';
                  const showSalary = tab === 'employees' || tab === 'salary' || tab === 'bonus';
                  return (
                    <div key={employee.id} className="pmh-card">
                      <button type="button" className="pmh-card-head" onClick={() => onOpen(employee)}>
                        {employee.photo_url ? (
                          <img className="pmh-avatar" src={`${imageHost}${employee.photo_url}`} alt="" />
                        ) : (
                          <span className="pmh-avatar is-initial">{employee.name?.charAt(0) || '?'}</span>
                        )}
                        <span className="pmh-name-block">
                          <span className="pmh-name">{employee.name}</span>
                          {employee.position && <span className="pmh-role">{employee.position}</span>}
                        </span>
                        {tab !== 'attendance' && (
                          <span className="pmh-total">
                            <span>Toplam</span>
                            <strong>{tl(total)}</strong>
                          </span>
                        )}
                        <ChevronRight size={16} className="pmh-chevron" />
                      </button>

                      {row.bonusDebt && (tab === 'employees' || tab === 'bonus') && (
                        <div className="pmh-debt">
                          <AlertTriangle size={14} />
                          {monthNames[row.bonusDebt.period_month - 1]} primi ödenmedi:
                          <strong>₺{row.bonusDebt.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</strong>
                        </div>
                      )}

                      <div className="pmh-metrics">
                        {metricsFor(tab, { ...row, employee }).map(m => (
                          <div key={m.label} className="pmh-metric">
                            <span>{m.label}</span>
                            <strong className={m.tone ? `pmh-tone-${m.tone}` : ''}>{m.value}</strong>
                          </div>
                        ))}
                      </div>

                      <div className="pmh-actions">
                        {showAttendance && (
                          <>
                            <button
                              type="button"
                              onClick={() => onAbsentToday(employee)}
                              disabled={!!row.empAttendance?.status}
                              className="is-red"
                            >
                              <UserX size={15} /> {row.empAttendance?.status ? 'İşaretli' : 'Gelmedi'}
                            </button>
                            <button type="button" onClick={() => onAttendance(employee)} className="is-amber">
                              <ClipboardPlus size={15} /> Yoklama
                            </button>
                            <button type="button" onClick={() => onOvertime(employee)} className="is-blue">
                              <Clock size={15} /> Mesai
                            </button>
                          </>
                        )}
                        {showSales && (
                          <button type="button" onClick={() => onSalesHistory(employee)} className="is-indigo">
                            <ShoppingCart size={15} /> Satışlar
                          </button>
                        )}
                        {showSalary && (
                          <button type="button" onClick={() => onSalaryDetail(employee)} className="is-purple">
                            <BarChart3 size={15} /> Maaş Detay
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div className="pmh-revenue">
                  Aylık Ciro (Tamirat Dahil) <strong>{tl(group.revenue)}</strong>
                </div>
              </div>
            ))
          )}
        </>
      )}
    </div>
  );
};

export default PersonnelMobileHome;
